import React, { useState, useEffect } from 'react';
import { homeApi } from '../../api/api';
import { AdminFieldInput, AdminUrlField } from '../components/AdminUI';

const PLATFORMS = [
  {
    key: 'social_facebook',
    id: 'facebook',
    title: 'Facebook Community',
    icon: 'fab fa-facebook-f',
    iconColor: 'text-blue-500',
    defaults: {
      url: 'https://www.facebook.com/radhekrishnashastri.bapu',
      stat1Value: '15K+',
      stat1Label: 'Followers',
      stat2Value: 'Daily',
      stat2Label: 'Updates',
    },
  },
  {
    key: 'social_instagram',
    id: 'instagram',
    title: 'Instagram',
    icon: 'fab fa-instagram',
    iconColor: 'text-purple-500',
    defaults: {
      url: 'https://www.instagram.com/radhekrishnabapu_official',
      stat1Value: '10K+',
      stat1Label: 'Followers',
      stat2Value: '1K+',
      stat2Label: 'Posts',
    },
  },
  {
    key: 'social_youtube',
    id: 'youtube',
    title: 'YouTube Channel',
    icon: 'fab fa-youtube',
    iconColor: 'text-red-500',
    defaults: {
      url: 'https://www.youtube.com/@Radhekrishnabapuofficial',
      stat1Value: '25K+',
      stat1Label: 'Subscribers',
      stat2Value: '150+',
      stat2Label: 'Videos',
      stat3Value: '2M+',
      stat3Label: 'Views',
    },
  },
];

function pickStat(stats, index, raw, valueKey, labelKey, defaults) {
  const fromArr = stats[index] || {};
  return {
    value: (fromArr.value != null ? String(fromArr.value) : null)
      || (raw[valueKey] != null ? String(raw[valueKey]) : defaults[valueKey])
      || '',
    label: (fromArr.label != null ? String(fromArr.label) : null)
      || (raw[labelKey] != null ? String(raw[labelKey]) : defaults[labelKey])
      || '',
  };
}

function normalizePlatform(raw, defaults) {
  const d = raw && typeof raw === 'object' ? raw : {};
  const stats = Array.isArray(d.stats) ? d.stats : [];
  const s1 = pickStat(stats, 0, d, 'stat1Value', 'stat1Label', defaults);
  const s2 = pickStat(stats, 1, d, 'stat2Value', 'stat2Label', defaults);
  const next = {
    url: (d.url != null ? String(d.url) : defaults.url) || '',
    stat1Value: s1.value,
    stat1Label: s1.label,
    stat2Value: s2.value,
    stat2Label: s2.label,
  };
  if (defaults.stat3Value != null || defaults.stat3Label != null) {
    const s3 = pickStat(stats, 2, d, 'stat3Value', 'stat3Label', defaults);
    next.stat3Value = s3.value;
    next.stat3Label = s3.label;
  }
  return next;
}

const SocialMediaAdmin = () => {
  const [forms, setForms] = useState(() =>
    Object.fromEntries(PLATFORMS.map((p) => [p.id, { ...p.defaults }]))
  );
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    setLoading(true);
    homeApi
      .getAll()
      .then((res) => {
        if (res.success && res.data) {
          const d = res.data;
          setForms((prev) => {
            const next = { ...prev };
            PLATFORMS.forEach((p) => {
              next[p.id] = normalizePlatform(d[p.key], p.defaults);
            });
            return next;
          });
        } else if (res.message) {
          setError(res.message);
        }
      })
      .catch(() => setError('Failed to load social media settings'))
      .finally(() => setLoading(false));
  }, []);

  const setField = (id, field, value) => {
    setForms((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const savePlatform = async (platform) => {
    const form = forms[platform.id] || platform.defaults;
    setSaving((prev) => ({ ...prev, [platform.id]: true }));
    setError('');
    try {
      const stats = [
        { value: (form.stat1Value || '').trim(), label: (form.stat1Label || '').trim() },
        { value: (form.stat2Value || '').trim(), label: (form.stat2Label || '').trim() },
      ];
      if (platform.defaults.stat3Value != null || platform.defaults.stat3Label != null) {
        stats.push({
          value: (form.stat3Value || '').trim(),
          label: (form.stat3Label || '').trim(),
        });
      }
      await homeApi.updateKey(platform.key, {
        url: (form.url || '').trim(),
        stats,
      });
      showToast(`${platform.title} saved`);
    } catch {
      setError(`Failed to save ${platform.title}`);
    } finally {
      setSaving((prev) => ({ ...prev, [platform.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <i className="fas fa-spinner fa-spin text-orange-500 text-2xl"></i>
      </div>
    );
  }

  const renderStatBlock = (platformId, form, n, placeholderValue, placeholderLabel) => (
    <div className="space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Stat {n}</p>
      <AdminFieldInput
        label="Value"
        value={form[`stat${n}Value`] || ''}
        onChange={(e) => setField(platformId, `stat${n}Value`, e.target.value)}
        onClear={() => setField(platformId, `stat${n}Value`, '')}
        clearable
        placeholder={placeholderValue}
      />
      <AdminFieldInput
        label="Label"
        value={form[`stat${n}Label`] || ''}
        onChange={(e) => setField(platformId, `stat${n}Label`, e.target.value)}
        onClear={() => setField(platformId, `stat${n}Label`, '')}
        clearable
        placeholder={placeholderLabel}
      />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-gray-800 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-medium">
          {toast}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800">Social Media</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Links and stats for Home community cards (YouTube also powers Katha page stats)
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {PLATFORMS.map((platform) => {
          const form = forms[platform.id] || platform.defaults;
          const isSaving = !!saving[platform.id];
          const hasViews = platform.defaults.stat3Value != null || platform.defaults.stat3Label != null;
          return (
            <div
              key={platform.id}
              className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 md:p-6 flex flex-col${
                platform.id === 'youtube' ? ' md:col-span-2' : ''
              }`}
            >
              <h3 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
                <i className={`${platform.icon} ${platform.iconColor}`}></i>
                {platform.title}
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                {hasViews
                  ? 'Profile link plus Subscribers, Videos, and Views.'
                  : 'Profile link and the two stats on this card.'}
              </p>

              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Profile URL
                </label>
                <AdminUrlField
                  value={form.url}
                  onChange={(e) => setField(platform.id, 'url', e.target.value)}
                  onClear={() => setField(platform.id, 'url', '')}
                  placeholder="https://..."
                />

                <div className={`grid grid-cols-1 md:grid-cols-2${hasViews ? ' lg:grid-cols-3' : ''} gap-4 mt-5`}>
                  {renderStatBlock(platform.id, form, 1, 'e.g. 25K+', 'e.g. Subscribers')}
                  {renderStatBlock(platform.id, form, 2, 'e.g. 150+', 'e.g. Videos')}
                  {hasViews && renderStatBlock(platform.id, form, 3, 'e.g. 2M+', 'e.g. Views')}
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => savePlatform(platform)}
                  disabled={isSaving}
                  className="admin-home-save-btn disabled:opacity-60"
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SocialMediaAdmin;
